import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { Subject } from 'rxjs';
import { SolutionAction } from './solution.state';
import { QuestionnaireConfigState } from './questionnaire.config.state';
import { QuestionnaireConfig } from 'app/@types/dto/questionnaire-config.model';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { FormulaireCallCompletion } from '../apps/components/settings-form/settings-form.component';
import { ResultsFetcherService } from '@services/results-fetcher/results-fetcher.service';

export interface QuestionnaireModel extends BaseStateInterface {
    questionnaire: QuestionnaireDto | undefined;
    questionInstancePerQuestionCode: Map<string, QuestionInstanceDto[]> | undefined;
    visitedPage: string | undefined;
    externalUpdates: Set<string>;
}

export namespace QuestionnaireAction {
    export class Activate {
        public static type = '[Questionnaire UI] Activate Questionnaire';

        constructor(
            public organizationId: string,
            public year: number,
            public entityId: string,
            public isOnboarding?: boolean,
            public onboardingAwaiter?: Subject<boolean>,
        ) {}
    }

    export class CreateUpdateAction {
        public static type = '[Questionnaire UI] CreateUpdate QuestionInstance';

        constructor(
            public organizationId: string,
            public instance: QuestionInstanceDto,
            public questionField: number,
            public callCompletion: Subject<QuestionInstanceDto | undefined>,
        ) {}
    }

    export class DeleteAction {
        public static type = '[Questionnaire UI] Delete QuestionInstance';

        constructor(
            public organizationId: string,
            public instance: QuestionInstanceDto,
            public callCompletion: Subject<QuestionInstanceDto[] | undefined>,
        ) {}
    }

    export class SetVisitedPage {
        public static type = '[Questionnaire UI] SetVisitedPage';

        constructor(public pageCode: string) {}
    }

    export class UpdatePropertyAction {
        public static type = '[Questionnaire UI] Update Questionnaire';

        constructor(
            public organizationId: string,
            public instance: QuestionnaireDto,
            public callCompletion: Subject<FormulaireCallCompletion>,
        ) {}
    }
}

@State<QuestionnaireModel>({
    name: 'questionnaire',
    defaults: {
        questionnaire: undefined,
        questionInstancePerQuestionCode: undefined,
        visitedPage: undefined,
        isInit: false,
        isLoading: false,
        externalUpdates: new Set<string>(),
        errors: [],
    },
})
@Injectable()
export class QuestionnaireState {
    private isInit: boolean = false;

    constructor(
        private questionnaireService: QuestionnaireHttpService,
        private store: Store,
        private resultsFetcherService: ResultsFetcherService,
    ) {}

    //----- store action -----//

    @Action(QuestionnaireAction.Activate)
    activate(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.Activate) {
        if (!this.isInit || action.isOnboarding || this.isInit) {
            ctx.patchState({
                isLoading: true,
            });
            this.questionnaireService.get(action.organizationId, action.year, action.entityId).subscribe((qqi) => {
                if (qqi) {
                    let questionMap = qqi.questionInstances.reduce((reducer, qi) => {
                        let entries = reducer.get(qi.questionCode);
                        if (entries === undefined) reducer.set(qi.questionCode, [qi]);
                        else entries.push(qi);

                        return reducer;
                    }, new Map<string, QuestionInstanceDto[]>());

                    const updateQuestionnaire = qqi.questionnaire;
                    updateQuestionnaire.lastUpdate = new Date().toISOString();
                    this.questionnaireService.updateProperty(action.organizationId, updateQuestionnaire).subscribe({});
                    ctx.patchState({
                        questionnaire: updateQuestionnaire,
                        questionInstancePerQuestionCode: questionMap,
                        isInit: true,
                        isLoading: false,
                        errors: undefined,
                    });
                    this.questionnaireService.getResults(action.organizationId, qqi.questionnaire.id!).subscribe({
                        next: (results) => {
                            this.store.dispatch(
                                new SolutionAction.Activate(action.organizationId, qqi.questionnaire.id!),
                            );
                        },
                    });
                    Chathive.widget.setContextField({ key: 'questionnaireId', value: qqi.questionnaire.id });
                    if (action.isOnboarding && action.onboardingAwaiter) {
                        action.onboardingAwaiter.next(true);
                    }

                    this.isInit = true;
                } else {
                    let errors = ctx.getState().errors;
                    errors.push('Cannot fetch the questionnaire related entities');

                    ctx.patchState({
                        questionnaire: undefined,
                        questionInstancePerQuestionCode: new Map<string, QuestionInstanceDto[]>(),
                        isInit: true,
                        isLoading: false,
                        errors: errors,
                    });

                    if (action.isOnboarding && action.onboardingAwaiter) {
                        action.onboardingAwaiter.next(false);
                    }
                }
            });
        }
    }

    @Action(QuestionnaireAction.CreateUpdateAction)
    createOrUpdate(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.CreateUpdateAction) {
        this.questionnaireService.createUpdate(action.organizationId, action.instance).subscribe({
            next: (qia) => {
                const questions = new Map([...ctx.getState().questionInstancePerQuestionCode!]);
                const config = this.store.selectSnapshot(QuestionnaireConfigState.getStaticConfig)!;
                if (qia && qia.length !== 0) {
                    this.handleQuestionInstanceUpdate(config, questions, qia);
                    let externalUpdatedSet = new Set(
                        qia
                            .filter((qi) => qi.questionCode !== action.instance.questionCode)
                            .map((qi) => qi.questionCode),
                    );
                    ctx.patchState({
                        questionInstancePerQuestionCode: questions,
                        externalUpdates: externalUpdatedSet,
                    });
                    action.callCompletion.next(qia[0]);
                    this.resultsFetcherService.setInvalid();
                    this.store.dispatch(new SolutionAction.Invalidate());
                }
            },
            error: (error) => {
                const questions = new Map([...ctx.getState().questionInstancePerQuestionCode!]);
                action.callCompletion.next(undefined);
                ctx.patchState({
                    questionInstancePerQuestionCode: questions,
                });
            },
            complete: () => {},
        });
    }

    handleQuestionInstanceUpdate(
        config: QuestionnaireConfig,
        stateQuestionMapPerQuestionCode: Map<string, QuestionInstanceDto[]>,
        questionInstanceToUpdate: QuestionInstanceDto[],
    ): void {
        let questionTemplateByQuestionCode = Object.values(config.questionsPerPage)
            .flatMap((a) => Object.values(a).flatMap((qa) => qa))
            .reduce((map, qt) => map.set(qt.questionCode, qt), new Map<string, QuestionDto>());

        questionInstanceToUpdate.forEach((qi) => {
            const qt = questionTemplateByQuestionCode.get(qi.questionCode);
            let currentQuestionArray = stateQuestionMapPerQuestionCode.get(qi.questionCode);
            if (qt) {
                if (qt.isDynamic || qt.option?.isSubQuestion) {
                    if (currentQuestionArray) {
                        let index = qt.isDynamic
                            ? currentQuestionArray.findIndex((e) => e.value1 === qi.value1)
                            : currentQuestionArray.findIndex(
                                  (x) => x.relatedQuestionInstanceId === qi.relatedQuestionInstanceId,
                              );
                        if (index !== -1) currentQuestionArray.splice(index, 1, qi);
                        else currentQuestionArray.push(qi);
                    } else {
                        stateQuestionMapPerQuestionCode.set(qi.questionCode, [qi]);
                    }
                } else {
                    stateQuestionMapPerQuestionCode.set(qi.questionCode, [qi]);
                }
            }
        });
    }

    @Action(QuestionnaireAction.DeleteAction)
    delete(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.DeleteAction) {
        this.questionnaireService.delete(action.organizationId, action.instance).subscribe({
            next: (qia) => {
                const questions = new Map([...ctx.getState().questionInstancePerQuestionCode!]);
                const config = this.store.selectSnapshot(QuestionnaireConfigState.getStaticConfig)!;
                if (qia) {
                    if (action.instance.id) {
                        this.handleQuestionInstanceDelete(config, questions, qia);
                    }
                    action.callCompletion.next(qia);
                    let externalUpdatedSet = new Set(
                        qia
                            .filter((qi) => qi.questionCode !== action.instance.questionCode)
                            .map((qi) => qi.questionCode),
                    );
                    ctx.patchState({
                        questionInstancePerQuestionCode: questions,
                        externalUpdates: externalUpdatedSet,
                    });
                    this.resultsFetcherService.setInvalid();
                    this.store.dispatch(new SolutionAction.Invalidate());
                }
            },
            error: (error) => {
                const questions = new Map([...ctx.getState().questionInstancePerQuestionCode!]);
                action.callCompletion.next(undefined);
                ctx.patchState({
                    questionInstancePerQuestionCode: questions,
                });
            },
            complete: () => {},
        });
    }

    handleQuestionInstanceDelete(
        config: QuestionnaireConfig,
        stateQuestionMapPerQuestionCode: Map<string, QuestionInstanceDto[]>,
        questionInstanceToUpdate: QuestionInstanceDto[],
    ): void {
        let questionTemplateByQuestionCode = Object.values(config.questionsPerPage)
            .flatMap((a) => Object.values(a).flatMap((qa) => qa))
            .reduce((map, qt) => map.set(qt.questionCode, qt), new Map<string, QuestionDto>());
        questionInstanceToUpdate.forEach((qi) => {
            const qt = questionTemplateByQuestionCode.get(qi.questionCode);
            let currentQuestionArray = stateQuestionMapPerQuestionCode.get(qi.questionCode);
            if (qt) {
                if (qt.isDynamic || qt.option?.isSubQuestion) {
                    if (currentQuestionArray) {
                        let index = qt.isDynamic
                            ? currentQuestionArray.findIndex((e) => e.value1 === qi.value1)
                            : currentQuestionArray.findIndex(
                                  (x) => x.relatedQuestionInstanceId === qi.relatedQuestionInstanceId,
                              );
                        if (index !== -1) currentQuestionArray.splice(index, 1);
                    }
                } else {
                    stateQuestionMapPerQuestionCode.delete(qi.questionCode);
                }
            }
        });
    }

    @Action(QuestionnaireAction.UpdatePropertyAction)
    UpdateProperty(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.UpdatePropertyAction) {
        this.questionnaireService.updateProperty(action.organizationId, action.instance).subscribe({
            next: (updateQuestionnaire) => {
                ctx.patchState({ questionnaire: updateQuestionnaire });
                if (action.callCompletion) {
                    action.callCompletion.next('questSuccess');
                    action.callCompletion.complete();
                }
                this.resultsFetcherService.setInvalid();
                this.store.dispatch(new SolutionAction.Invalidate());
            },
            error: (error) => {
                if (action.callCompletion) {
                    action.callCompletion.next('questFail');
                    action.callCompletion.complete();
                }
            },
        });
    }

    @Action(QuestionnaireAction.SetVisitedPage)
    setVisitedQuestionCode(ctx: StateContext<QuestionnaireModel>, action: QuestionnaireAction.SetVisitedPage) {
        ctx.patchState({
            visitedPage: action.pageCode,
        });
    }

    @Selector([QuestionnaireState])
    static getQuestionInstances(state: QuestionnaireModel) {
        return state.questionInstancePerQuestionCode;
    }

    @Selector([QuestionnaireState])
    static getQuestionnaire(state: QuestionnaireModel) {
        return state.questionnaire;
    }

    @Selector([QuestionnaireState])
    static getVisitedPage(state: QuestionnaireModel) {
        return state.visitedPage;
    }

    @Selector([QuestionnaireState])
    static getExternalUpdate(state: QuestionnaireModel) {
        return state.externalUpdates;
    }
}
